import { Grid } from '@material-ui/core';
import { isNaN } from 'lodash';
import { useTranslation } from 'react-i18next';
import { closeAllItems } from 'src/all-items/all-items';
import React, { useRef, useState } from 'react';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import CashierPreferencesHelper from 'src/common/helpers/cashier-preferences-helper';
import { getDefaultWeightUomText } from 'src/common/helpers/localized-description-helper';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { IButton } from 'src/common/interfaces/IButton';
import { POSModeState } from 'src/common/interfaces/states/POSModeState';
import { store } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import Keypad from 'src/keypad/components/keypad';
import { closeSearchModal } from 'src/search/search';
import TareWeightApproval, { TareWeightApprovalHandle } from './components/tare-weight-approval';
import { TareWeightForm } from './components/tare-weight-form';
import { TareWeightPercentageForm } from './components/tare-weight-percentage-form';
import TareWeightTitle from './components/tare-weight-title';
import { styles } from './tare-weight-items-styles';

type Props = ITareWeigthItemsProps;
export const showTareWeightItemsScreen = (props: Props): void => {
  closeAllItems();
  closeSearchModal();
  setHomePage(<TareWeightItemsModal {...props} />);
};

const TareWeightItemsModal: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const trainingModeClasses = trainingModeStyles();
  const [isPercentage, setIsPercentage] = useState<boolean>(false);
  const [tareValue, setTareValue] = useState<string>('');
  const [itemWeight, setItemWeight] = useState<string>('');
  const posModeState: POSModeState = store.getState().posMode;
  const TARE_WEIGHT_KEYPAD_TOP_LABEL: string = t('POSKeyPadControl_ManualTareWeight');
  const isLeftHanded = CashierPreferencesHelper.isLeftHanded();
  const tareWeightApprovalRef = useRef<TareWeightApprovalHandle>(null);

  const onSubmitManualTare = async (): Promise<void> => {
    if (tareValue && !isNaN(tareValue)) {
      await props.onSubmit(Number(tareValue), isPercentage);
      tareWeightApprovalRef?.current?.clearValues();
    }
  };

  const keypadSubmitButton: IButton = {
    label: 'OK',
    onClick: onSubmitManualTare,
  };

  return (
    <Grid item className='fullHeight flexFull' data-testid='tare-weight-items'>
      <Grid
        item
        className={`${
          posModeState.trainingMode
            ? trainingModeClasses.trainingModeBackground
            : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.tareWeightBackground}`}
          data-testid='tare-weight-container'
        >
          <Grid
            item
            container
            className={
              posModeState.trainingMode
                ? trainingModeClasses.trainingModeBackground
                : classes.background
            }
          >
            <Grid container justifyContent='flex-end' className={classes.displayButtons}>
              <TareWeightTitle
                title={t('Tare')}
                itemWeight={itemWeight ? `${itemWeight} ${getDefaultWeightUomText()}` : ''}
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            direction={isLeftHanded ? 'row-reverse' : 'row'}
            className={classes.tareWeightContainerScreen}
          >
            <Grid item xs={9} className={classes.tareWeightForms}>
              {props.linkedGroupItems &&
                (!props.isTareModification || props.showPreDefinedOptionsForTareWeightItem) && (
                  <TareWeightForm
                    linkedGroupItems={props.linkedGroupItems}
                    onSelectProduct={props.onSelect}
                  />
                )}
              {props.showPreDefinedOptionsForTareWeightItem && props.tarePercentage && (
                <TareWeightPercentageForm
                  tarePercentage={props.tarePercentage}
                  handleSelection={props.onSelectPercentage}
                />
              )}
              {props.canManualTareWeight && (
                <TareWeightApproval
                  ref={tareWeightApprovalRef}
                  handleCancel={props.onClose}
                  handleApprove={onSubmitManualTare}
                  setTareValue={setTareValue}
                  setIsPercentage={setIsPercentage}
                  setItemWeight={setItemWeight}
                  grossWeight={props.grossWeight}
                  isManualTarePercentageEnabled={props.isManualTarePercentageEnabled}
                  linkedGroupItems={props.linkedGroupItems}
                  isTareModification={props.isTareModification}
                />
              )}
            </Grid>
            <Grid item xs={3} className={`${classes.keypadContainer} ${classes.marginSeparator}`}>
              <Keypad
                keyboardId='tare-weight-items'
                visible={true}
                topLabelText={TARE_WEIGHT_KEYPAD_TOP_LABEL}
                submitButton={keypadSubmitButton}
                disableQuantity={true}
                disableManualWeight={true}
                noInputField={true}
                keypadTestIdPrefix='tare-weight-items-'
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface ITareWeigthItemsProps {
  linkedGroupItems?: IItemLookup[];
  canManualTareWeight: boolean;
  isManualTarePercentageEnabled: boolean;
  isTareModification: boolean;
  onClose: () => void;
  onSubmit: (value: number, isPercentage: boolean) => Promise<void>;
  onSelect: (product: IItemLookup) => Promise<void>;
  onSelectPercentage: (tareValue: number) => void;
  grossWeight?: number;
  tarePercentage?: number[];
  showPreDefinedOptionsForTareWeightItem: boolean;
}
export default TareWeightItemsModal;
